import { SphereButton } from "@components/common/sphere-button";
import { FaroSimpleTextField } from "@components/common/faro-text-field/faro-simple-text-field";
import { FaroTextFieldMessage } from "@components/common/faro-text-field/faro-text-field-message";
import { LabelWithHelp } from "@components/common/label-with-help";
import {
  createMemberOption,
  createTeamOption,
} from "@components/common/members-autocomplete/members-autocomplete-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { Stack } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { addGroup } from "@store/groups/groups-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { teamsSelector } from "@store/teams/teams-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { useMemo, useState } from "react";
import { type AutoCompleteOption, Dialog } from "@flat-ui";
import { STACK_ERROR_SX } from "@components/common/dialog/faro-dialog";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useTeamUtils } from "@hooks/use-team-utils";
import { sortTeams } from "@utils/team-utils";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";

interface Props {
  /** List of all the available groups in the company */
  groups: FaroCoreApiTypes.GroupSummaryResponse[];
}

/**
 * Renders the create group button and the dialog to be used to create new groups.
 * This component does not take care of the authorization of the user to create groups.
 */
export function CreateGroup({ groups }: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorContext();
  const { isScreenSmOrSmaller } = useMediaQueryList();
  const companyId = useCompanyId();
  const companyMembers = useCompanyMembers();
  const currentUser = useAppSelector(currentUserSelector);
  const teams = useAppSelector(teamsSelector);
  const { isTeamName } = useTeamUtils();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /** Stores the name for the group to be created */
  const [groupName, setGroupName] = useState<string>("");

  /** Stores the names of the groups the user has access to. */
  const groupNames = groups.map((group) => group.name);

  /** Stores the members of the company in the format required by the autocomplete */
  const memberOptions: AutoCompleteOption[] = useMemo(() => {
    return sortMembersByNameOrEmail(companyMembers).map((member) => {
      /**
       * If the user is a group manager, it should be added to the group by default
       * and it should not be removed, otherwise the creator of the group would not have access to it.
       */
      const isPreselected =
        currentUser?.role === "COMPANY_MANAGER" &&
        currentUser?.identity === member.identity;

      return createMemberOption({
        member,
        isPreselected,
      });
    });
  }, [companyMembers, currentUser]);

  /** Reforming team data for autocomplete component */
  const teamOptions: AutoCompleteOption[] = useMemo(
    () =>
      sortTeams(teams, "name_ascending").map((team) =>
        createTeamOption({ team })
      ),
    [teams]
  );

  const autocompleteOptions: AutoCompleteOption[] = useMemo(
    () => [...teamOptions, ...memberOptions],
    [teamOptions, memberOptions]
  );

  const {
    selectedMembers: selectedManagers,
    setSelectedMembers: setSelectedManagers,
    uniqueSelectedValues: uniqueManagersIds,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset,
  } = useMembersAutocomplete({
    entityName: "group",
    existingMembers: [],
    options: autocompleteOptions,
    companyMemberIds: companyMembers.map((member) => member.identity),
  });

  /** Stores whether the create button should be disabled */
  const isCreateDisabled: boolean = useMemo(() => {
    // The first page is never disabled because the fields are validated
    return (
      !groupName ||
      shouldDisableSendInvite(uniqueManagersIds, isMemberInputValid)
    );
  }, [groupName, uniqueManagersIds, isMemberInputValid]);

  /** Resets the dialog content, and opens the dialog. */
  // eslint-disable-next-line @typescript-eslint/require-await -- Please review lint error
  async function onOpenDialog(): Promise<void> {
    // Add all the preselected members as group managers by default.
    const preselectedManagers = memberOptions.filter(
      (member) => member.isPreselected
    );
    setSelectedManagers(preselectedManagers);
    setIsDialogOpen(true);
  }

  function closeDialog(): void {
    reset();
    setGroupName("");
    setIsDialogOpen(false);
  }

  /** Triggered when the create button is clicked, creates the group and closes the dialog */
  async function handleConfirm(): Promise<void> {
    setIsLoading(true);
    try {
      await dispatch(
        addGroup({
          companyId,
          groupName,
          selectedGroupManagerIds: uniqueManagersIds,
        })
      ).unwrap();
    } catch (error) {
      handleErrorWithToast({
        id: `createGroup-${Date.now().toString()}`,
        title: "Error creating group",
        error,
      });
    } finally {
      setIsLoading(false);
      setIsDialogOpen(false);
    }
  }

  return (
    <>
      {/* Button to open the dialog */}
      <SphereButton
        onClick={onOpenDialog}
        sx={{ width: isScreenSmOrSmaller ? "100%" : "auto" }}
      >
        New group
      </SphereButton>

      {/* Create group dialog */}
      <Dialog
        title="New group"
        open={isDialogOpen}
        confirmText="Create"
        isConfirmDisabled={isCreateDisabled}
        onConfirm={handleConfirm}
        showSpinner={isLoading}
        onCancel={closeDialog}
      >
        {/* Group name */}
        <Stack sx={STACK_ERROR_SX}>
          <LabelWithHelp title="Group name" isRequired />
          <FaroSimpleTextField
            placeholder="Enter group name"
            size="small"
            fullWidth
            value={groupName}
            onChange={(event) => setGroupName(event.target.value)}
          />
          <FaroTextFieldMessage
            shouldHide={!groupNames.includes(groupName)}
            message={{
              type: "info",
              helperText: "Group name already exists",
            }}
          />
        </Stack>

        {/* Groups manager */}
        <Stack sx={STACK_ERROR_SX}>
          <MembersAutocomplete
            isRequired
            label="Group manager"
            helperText={helperText}
            options={autocompleteOptions}
            onInputChange={onInputChange}
            selectedOptions={selectedManagers}
            handleMemberSelect={handleMemberSelect}
            isValid={(value) => isNewEmailValid(value) || isTeamName(value)}
          />
        </Stack>
      </Dialog>
    </>
  );
}
